/* eslint-disable max-lines */
import { enFrequencyType } from 'enums/enFrequencyType';
import { enParameters } from 'enums/enParameters';
import { enNormalizedPaymentType, enPaymentMethod } from 'enums/enPaymentMethod';
import { enPaymentStatus } from 'enums/enPaymentStatus';
import { enPaymentType } from 'enums/enPaymentType';
import { ICartModel, IStuddentModel, IProductModel } from 'interfaces/ICartModel';
import { ICouponModel } from 'interfaces/ICouponModel';
import { IPlan } from 'interfaces/INext';
import { IPaymentResult } from 'interfaces/IPaymentResult';
import { enCurrency } from 'interfaces/IProductData';
import { IThankYouPage } from 'interfaces/IThankYouPage';
import { IUserAddressModel } from 'interfaces/IUserAddressModel';
import get from 'lodash/get';
import { Cookies } from 'react-cookie';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/Observable';
import { ReplaySubject } from 'rxjs/ReplaySubject';
import { cartService } from 'services/CartService';
import { paymentProgressService } from 'services/PaymentProgressService';
import { ADYEN_KEY, GOOFY_ID, MAX_CREDIT_CARDS, MERCADO_PAGO_KEY } from 'settings';
import { round10, round2 } from 'shared/functions';
import { apiService } from 'shared/services';

import { IPaymentModel } from '../interfaces/IPaymentModel';
import { showAlert } from './../shared/services/dialog';
import { brandService } from './BrandService';
import { hotJarService } from './HotJar';
import { getInstallments } from './InstallmentsService';
import { getCitiesByState } from './LocationService';
import { parameterService } from './ParameterService';
import { currencyService } from './CurrencyService';
import shopifyService from './ShopifyService';
import { combineLatest } from 'rxjs/observable/combineLatest';

export class PaymentService {
  private state: ICartModel = {
    payment: [{}]
  };

  private data$: BehaviorSubject<ICartModel>;
  private lockPayment$: BehaviorSubject<number>;
  private paymentResult$ = new ReplaySubject<IPaymentResult>(1);
  private paymentSent$ = new BehaviorSubject<boolean>(false);
  private blockPayment$ = new BehaviorSubject<boolean>(false);
  private blockAddressAfterPayment$ = new BehaviorSubject<boolean>(false);

  private hasPaymentResult = false;
  private paymentInProgress = false;

  constructor(state: ICartModel = null) {
    if (state) {
      this.state = state;
    }

    this.lockPayment$ = new BehaviorSubject(0);
    this.data$ = new BehaviorSubject(this.state);

    Observable.combineLatest(parameterService.get(enParameters.df)).subscribe(([df]) => {
      df = (df || '').replace(/[^0-9]/, '');
      if (df.length === 11) {
        this.setData({ document: df });
      }
    });

    this.data$
      .asObservable()
      .map(state => state.payment.map(p => p.value))
      .filter(v => v[0] != null)
      .map(v => JSON.stringify(v))
      .debounceTime(500)
      .distinctUntilChanged()
      .switchMap(() => this.data$.asObservable().take(1))
      .switchMap(state => {
        return Observable.combineLatest(getInstallments(state.payment.map(p => p.value)), Observable.of(state), currencyService.getCurrency());
      })
      .filter(([, , currency]) => currency === enCurrency.BRL)
      .subscribe(([baseInstallments, state]) => {
        let installments = Object.values(baseInstallments);
        state.payment.map((p, key) => {
          const value = p.value;

          const i = installments.filter(installment => {
            installment.installments.sort((a, b) => b.value - a.value);
            return installment.installments[0].value == value;
          });

          if (i.length === 0) {
            return;
          }

          const installment = i[0];

          const selected = get(this.state.payment[key], 'numberInstallments', 0);

          this.setPaymentData(key, {
            installments: installment.installments,
            numberInstallments: installment.maxInstallments >= selected ? selected : installment.maxInstallments
          });
        });
      });

    this.emit();
  }

  addLock() {
    this.lockPayment$.next(this.lockPayment$.value + 1);
  }

  removeLock() {
    if (this.lockPayment$.value <= 0) {
      this.lockPayment$.next(0);
      return;
    }

    this.lockPayment$.next(this.lockPayment$.value - 1);
  }

  hasPaymentInProgress(): boolean {
    return this.paymentInProgress;
  }

  hasPaymentSent(): Observable<boolean> {
    return this.paymentSent$.asObservable().distinctUntilChanged();
  }

  pay(): Observable<IPaymentResult> {
    this.paymentInProgress = true;
    this.paymentSent$.next(true);

    paymentProgressService.setPaymentProgressStatus({
      status: enPaymentStatus.PROGRESS
    });

    return this.lockPayment$
      .filter(lock => lock === 0)
      .take(1)
      .switchMap(() => cartService.getCart())
      .filter(data => !data.isCached)
      .map(cart => cart.price)
      .map(price => {
        if (this.state.payment.length > 1) {
          const sumValuePayment = round10(this.state.payment.reduce((acc, p) => acc + p.value, 0));
          if (sumValuePayment < round10(price)) {
            return Observable.throw(new Error('Por favor, verifique a soma dos valores dos cartões.'));
          }
        }

        return true;
      })
      .switchMap(() => combineLatest(cartService.getCart(), parameterService.getAll(), this.getCart(), this.normalizePaymentData()))
      .take(1)
      .switchMap(([cart, parameters, payment, data]) => {
        const paymentMethod: any = data.payment[0].paymentMethod;

        let url = '/pay/creditcard';

        if (paymentMethod === enNormalizedPaymentType.BANKSLIP) {
          url = '/pay/bankslip';
        }

        if (paymentMethod === enNormalizedPaymentType.FREE) {
          url = '/pay/free';
        }

        if (paymentMethod === enNormalizedPaymentType.PAYPAL) {
          try {
            const cookie = new Cookies();

            cookie.set(`pixel-${cart.contentId}`, true, {
              maxAge: 60 * 60 * 24 * 90
            });
          } catch (err) {}

          url = '/pay/paypal';
        }

        if (cart.config.isOneClick) {
          url = '/pay/oneclickbuy';
        }

        if (payment.next && payment.next.plan && Number(payment.next.plan.trialDays) > 0) {
          url = '/pay/creditcardtrial';
        }

        if (payment.next && payment.next.plan && Number(payment.next.plan.trialDays) > 0 && paymentMethod === enNormalizedPaymentType.BANKSLIP) {
          url = '/pay/trial';
        }

        if (cart.subscription && cart.subscription.hasTrial) {
          url = '/pay/creditcardtrial';
        }

        if (cart.subscription && cart.subscription.hasTrial && paymentMethod === enNormalizedPaymentType.BANKSLIP) {
          url = '/pay/trial';
        }

        const body: any = {
          transactionKey: data.ssid,
          ead: cart.config.ead,
          goofyId: GOOFY_ID,
          contentId: data.contentId,
          document: data.document,
          currency: cart.config.currency,
          email: data.email,
          fullPrice: data.fullPrice,
          name: data.name,
          studdent: cart.config.ead ? data.studdent : null,
          same: data.samePerson,
          payment: data.payment.map(p => {
            return {
              brand: p.brand,
              creditCard: p.creditCard,
              paymentMethod: p.paymentMethod,
              saveCard: p.saveCard,
              value: p.value,
              expires: p.expires,
              cvv: p.cvv,
              name: p.name,
              installments: cart.config.currency === 'USD' ? 1 : p.numberInstallments || 1,
              document: p.document,
              adyenHash: p.adyenHash,
              mpHash: p.mpHash
            };
          }),
          personType: data.personType,
          phone: data.phone,
          productType: data.productType,
          quantity: data.quantity,
          captcha: data.captchaToken || null,
          paymentMethod,
          parameters
        };

        if (cart.config.ead && data.samePerson) {
          body.ead = true;
          body.email = data.studdent.email;
          body.name = data.studdent.name;
          body.phone = data.studdent.phone;
          body.document = data.studdent.document;
        }

        if (data.shipping) {
          body.shipping = data.shipping;
        }

        if (data.address && data.address.postalCode) {
          body.address = {
            cityId: data.address.cityId || null,
            street: data.address.street || null,
            complement: data.address.complement || null,
            district: data.address.district || null,
            postalCode: data.address.postalCode || null,
            number: data.address.number || null,
            province: data.address.province || null,
            countryId: data.address.country || null
          };
        }

        if (data.next) {
          body.next = data.next;
        }

        return apiService.post<IPaymentResult>(url, body, null, { maxAttempts: 1 }).map(r => {
          this.hasPaymentResult = true;

          r.personType = data.personType;
          r.brand = data.payment[0].brand;

          shopifyService.clearCartData();
          this.paymentResult$.next(r);

          return r;
        });
      })
      .catch(err => {
        return Observable.throw(err);
      })
      .finally(() => {
        this.setData({ captchaToken: null });
        this.paymentInProgress = false;
      })
      .logError();
  }

  hasPayment() {
    return this.hasPaymentResult;
  }

  switchPersonType() {
    this.setData({
      personType: this.state.personType === 'E' ? 'F' : 'E'
    });
  }

  loadPaymentByHash(transactionKey: string, parameters: any): Observable<IThankYouPage> {
    return apiService.get(`/thankyou/${transactionKey}`, parameters).logError();
  }

  getPaymentResult(): Observable<IPaymentResult> {
    return this.paymentResult$.asObservable();
  }

  getCart(): Observable<ICartModel> {
    return this.data$.asObservable().distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getCurrentCart(): ICartModel {
    return JSON.parse(JSON.stringify(this.data$.value));
  }

  getProduct(index: number) {
    return this.getCart()
      .filter(c => !!c && !!c.products && c.products.length > 0)
      .map(c => c.products[index])
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getPayment(index: number) {
    return this.getCart()
      .map(c => c.payment[index])
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getPaymentInstallments(index: number) {
    return this.getCart()
      .filter(cart => !!cart.payment[index])
      .map(cart => cart.payment[index])
      .map(payment => {
        return {
          installments: payment.installments,
          numberInstallments: payment.numberInstallments
        };
      })
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  isMultipleCreditCards(): Observable<boolean> {
    return this.getCart().map(cart => cart.payment.length > 1);
  }

  getAddress() {
    return this.getCart()
      .map(c => c.address || {})
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  getCoupon() {
    return this.getCart()
      .map(c => c.coupon || {})
      .distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b));
  }

  setData(obj: Partial<ICartModel> = {}): void {
    if (obj.payment) {
      throw new Error('Invalid property, use setPaymentData');
    }

    if (obj.address) {
      throw new Error('Invalid property, use setAddressData');
    }

    if (obj.personType && obj.personType === 'E' && this.state.payment && this.state.payment[0].paymentMethod == enPaymentMethod.BOLETO) {
      this.setPaymentData(0, { paymentMethod: null });
    }

    this.state = { ...this.state, ...obj };

    this.emit();
  }

  setStuddentData(obj: Partial<IStuddentModel> = {}): void {
    const currentStuddent = this.state.studdent || {};

    const studdentData = {
      studdent: { ...currentStuddent, ...obj }
    };

    this.state = { ...this.state, ...studdentData };

    this.emit();
  }

  setProductData(index: number, obj: Partial<IProductModel> = {}) {
    const product = this.state.products[index];

    this.state.products[index] = { ...product, ...obj };
    this.emit();
  }

  setPaymentData(index: number, obj: Partial<IPaymentModel> = {}) {
    const payment = this.state.payment[index];

    if (obj.creditCard) {
      obj.brand = brandService.getCardBrandByNumber(obj.creditCard);
    }
    this.state.payment[index] = { ...payment, ...obj };
    this.emit();
  }

  handleChangeSaveCard(index: number, saveCard: boolean) {
    const currentPayments = this.state.payment;
    currentPayments.map((payment, i) => {
      this.state.payment[i] = {
        ...payment,
        saveCard: index === i ? saveCard : false
      };
    });

    this.emit();
  }

  addPayment(): Observable<any> {
    if (this.state.payment.length >= MAX_CREDIT_CARDS) {
      return Observable.empty();
    }

    return cartService
      .getCart()
      .take(1)
      .map(cart => cart.price)
      .switchMap(price => {
        const currentIndex = this.state.payment.length - 1;
        const currentPayment = this.state.payment[currentIndex];

        const lastValue = currentPayment.value;
        const newValue = round10(lastValue / 2);

        currentPayment.value = newValue;

        let sumValueAllPayments = newValue;
        this.state.payment.forEach(payment => {
          sumValueAllPayments += payment.value;
        });

        const valueNewPayment = round10(newValue + price - sumValueAllPayments);

        return Observable.combineLatest(
          getInstallments([newValue, valueNewPayment]),
          Observable.of(currentPayment),
          Observable.of(valueNewPayment),
          parameterService.get(enParameters.pf)
        );
      })
      .map(([installments, currentPayment, valueNewPayment, pf]) => {
        const _installments = Object.entries(installments);

        const installmentsNewPayment = _installments.find(i => Number(i[0]) === valueNewPayment)[1];
        const installmentscurrentPayment = _installments.find(i => Number(i[0]) === currentPayment.value)[1];

        currentPayment.installments = installmentscurrentPayment.installments;
        currentPayment.numberInstallments = installmentscurrentPayment.maxInstallments;

        if (pf === '1') {
          currentPayment.installments = [{ installment: 0, value: 0 }];
          currentPayment.numberInstallments = 0;
        }

        const newPayment = {
          value: round2(valueNewPayment),
          installments: installmentsNewPayment.installments,
          numberInstallments: installmentsNewPayment.maxInstallments
        };

        const payment = [...this.state.payment, newPayment];
        this.state.payment = payment;

        this.emit();
      });
  }

  removePayment(index: number, numberCreditCards: number = 1) {
    if (this.state.payment.length > 1) {
      const payment = this.state.payment;
      payment.splice(index, numberCreditCards);

      this.state.payment = payment;
      this.emit();

      if (payment.length == 1) {
        cartService
          .getCart()
          .take(1)
          .map(cart => cart.price)
          .subscribe(price => {
            payment[0].value = price;
            this.state.payment = payment;
            this.emit();
          });
      }
    }
  }

  setSelectedPlan = (plan: IPlan) => {
    cartService
      .getCart()
      .switchMap(cart => {
        return apiService.put(`/plan/${cart.transactionKey}/${cart.contentId}`, plan);
      })
      .loader()
      .subscribe(() => {
        this.setData({ next: { plan } });
      });
  };

  setAddressData(obj: Partial<IUserAddressModel> = {}) {
    const address = this.state.address || {};
    this.state.address = { ...address, ...obj };

    this.getCart()
      .combineLatest(cartService.getCart())
      .take(1)
      .filter(([payment, cart]) => payment.personType != 'E' || !cart.config.allowForeignShipping)
      .switchMap(() => getCitiesByState(this.state.address.province))
      .take(1)
      .subscribe(cities => {
        const city = cities.find(c => c.name.toLowerCase() === get(this.state, 'address.city', '').toLowerCase());

        if (city) {
          this.state.address.city = city.name;
          this.state.address.cityId = city.id;
        }
        this.emit();
      });

    this.getCart()
      .take(1)
      .filter(cart => cart.personType == 'E')
      .subscribe(() => {
        this.emit();
      });
  }

  setCoupon(obj: Partial<ICouponModel> = {}) {
    const coupon = this.state.coupon || {};
    this.state.coupon = { ...coupon, ...obj };
    this.emit();
  }

  showPaymentMultipleCardsButton(): Observable<boolean> {
    return cartService
      .getCart()
      .take(1)
      .combineLatest(this.getCart(), currencyService.getCurrency())
      .switchMap(([cart, payment, currency]) => {
        if (currency !== enCurrency.BRL) {
          this.removePayment(1, payment.payment.length - 1);

          return Observable.of(false);
        }

        if (cart.config.paymentType === enPaymentType.FREE) {
          return Observable.of(false);
        }

        const isMultipleCreditCards = cart.config.multipleCreditCards;
        const hasOnePaymentForm = payment.payment.length <= 1;
        const boletoPaypalSelected =
          payment.payment[0].paymentMethod == enPaymentMethod.BOLETO || payment.payment[0].paymentMethod == enPaymentMethod.PAYPAL;

        return Observable.of(isMultipleCreditCards && hasOnePaymentForm && !boletoPaypalSelected);
      });
  }

  public loadParameters() {
    parameterService.getAll().subscribe(parameters => {
      Object.keys(parameters).map(p => {
        if (p === enParameters.doc) {
          this.setData({ document: parameters[p] });
        } else if (p === enParameters.name || p === enParameters.nome) {
          this.setData({ name: parameters[p] });
        } else if (p === enParameters.email) {
          this.setData({ email: parameters[p] });
        } else if (p === enParameters.cel || p === enParameters.phone) {
          this.setData({ phone: parameters[p] });
        } else if (p === enParameters.cep || p === enParameters.zip) {
          let postalCode = parameters[p];

          if (!(postalCode.indexOf('-') > -1) && postalCode.length == 8) {
            postalCode = postalCode.substr(0, 5) + '-' + postalCode.substr(4 + 1);
          }

          this.setAddressData({ postalCode });
        } else if (p === enParameters.num) {
          this.setAddressData({ number: parameters[p] });
        } else if (p === enParameters.comp) {
          this.setAddressData({ complement: parameters[p] });
        } else if (p === enParameters.p) {
          this.setDefaultInstallment(Number(parameters[p]));
        } else if (p === enParameters.pf) {
          this.setPaymentData(0, { numberInstallments: 0 });
        }
      });
    });

    parameterService
      .get(enParameters.cupom)
      .combineLatest(cartService.getCart())
      .take(1)
      .filter(([coupon, cart]) => !!coupon && cart.config.paymentType !== enPaymentType.DONATION)
      .switchMap(([coupon]) => cartService.setCoupon(coupon))
      .subscribe(
        () => {},
        () => {
          showAlert('Há algo errado com o seu cupom, por favor entre em contato com o vendedor', 'Ops!').subscribe();
        }
      );
  }

  public setDefaultInstallment(installmentSelected: number) {
    const maxNumberInstallment = Math.max.apply(Math, this.state.payment[0].installments.map(i => i.installment));
    const numberInstallments = maxNumberInstallment >= installmentSelected ? installmentSelected : maxNumberInstallment;

    this.setPaymentData(0, { numberInstallments });
  }

  public getNormalizedFrequencyType(type: enFrequencyType, frequency: number) {
    if (type == enFrequencyType.DIARIO) {
      return frequency > 1 ? 'dias' : 'dia';
    }

    if (type == enFrequencyType.MENSAL) {
      return frequency > 1 ? 'meses' : 'mês';
    }

    if (type == enFrequencyType.ANUAL) {
      return frequency > 1 ? 'anos' : 'ano';
    }

    return '';
  }

  public getNormalizedFrequencyTypeEnUs(type: enFrequencyType, frequency: number) {
    if (type == enFrequencyType.DIARIO) {
      return frequency > 1 ? 'days' : 'day';
    }

    if (type == enFrequencyType.MENSAL) {
      return frequency > 1 ? 'months' : 'month';
    }

    if (type == enFrequencyType.ANUAL) {
      return frequency > 1 ? 'years' : 'year';
    }

    return '';
  }

  public getNormalizedPaymentType(type: enPaymentMethod): enNormalizedPaymentType {
    if (type === enPaymentMethod.PAYPAL) {
      hotJarService.log('paypal', true);
      return enNormalizedPaymentType.PAYPAL;
    }

    if (type === enPaymentMethod.BOLETO) {
      hotJarService.log('bankslip', true);
      return enNormalizedPaymentType.BANKSLIP;
    }

    if (type === enPaymentMethod.FREE) {
      hotJarService.log('free', true);
      return enNormalizedPaymentType.FREE;
    }

    if (type === enPaymentMethod.ONE_CLICK_BUY) {
      hotJarService.log('oneClick', true);
      return enNormalizedPaymentType.ONE_CLICK;
    }

    hotJarService.log('creditCard', true);
    return enNormalizedPaymentType.CREDIT_CARD;
  }

  public setBlockPayment(blockPayment: boolean): void {
    this.blockPayment$.next(blockPayment);
  }

  public shouldBlockPayment(): Observable<boolean> {
    return this.blockPayment$.asObservable().distinctUntilChanged();
  }

  public setBlockAddressAfterPayment(blockAddressAfter: boolean): void {
    this.blockAddressAfterPayment$.next(blockAddressAfter);
  }

  public shouldBlockAddressAfterPayment(): Observable<boolean> {
    return this.blockAddressAfterPayment$.asObservable().distinctUntilChanged();
  }

  private createCardTokenMercaodPago(cardInfo: any): Promise<string> {
    window.Mercadopago.setPublishableKey(MERCADO_PAGO_KEY);

    return new Promise<string>(resolve => {
      window.Mercadopago.clearSession();
      window.Mercadopago.createToken(cardInfo, (status: any, body: any) => {
        if (status < 200 || status > 299) {
          resolve('');
          return;
        }
        resolve(body.id as string);
      });
    });
  }

  private async generatePaymentTokens(data: ICartModel): Promise<ICartModel> {
    const adyen = await import('adyen-cse-web');

    const cse = adyen.createEncryption(ADYEN_KEY, {});

    for (const payment of data.payment) {
      if (!payment.creditCard) {
        continue;
      }

      payment.secret = `${payment.creditCard.substring(0, 2)}** **** **** **${payment.creditCard.substring(payment.creditCard.length - 2)}`;

      const [month, year] = (payment.expires || '').split('/');

      const adyenCartData = {
        number: payment.creditCard,
        cvc: payment.cvv,
        holderName: payment.name,
        expiryMonth: month,
        expiryYear: `20${year}`,
        generationtime: new Date().toISOString()
      };

      const adyenHash = cse.encrypt(adyenCartData);

      const mercadoPagoToken = await this.createCardTokenMercaodPago({
        email: data.email,
        cardNumber: payment.creditCard,
        securityCode: payment.cvv,
        cardExpirationMonth: month,
        cardExpirationYear: `20${year}`,
        cardholderName: payment.name,
        docType: (payment.document || data.document || '').length === 14 ? 'CNPJ' : 'CPF',
        docNumber: payment.document || data.document
      });

      payment.adyenHash = adyenHash;
      payment.mpHash = mercadoPagoToken;
    }

    return data;
  }

  private normalizePaymentData(): Observable<ICartModel> {
    const data: ICartModel = JSON.parse(JSON.stringify(this.state));

    return Observable.of(data).switchMap(data => {
      if (!data.personType) {
        data.personType = (data.document || '').length == 14 ? 'J' : 'F';
      }

      for (const payment of data.payment) {
        payment.paymentMethod = this.getNormalizedPaymentType(payment.paymentMethod) as any;
      }

      const hasCard = data.payment.find(p => !!p.creditCard);

      if (!hasCard) {
        return Observable.of(data);
      }

      return Observable.fromPromise(this.generatePaymentTokens(data));
    });
  }

  private emit() {
    const data = JSON.parse(JSON.stringify(this.state));
    this.data$.next(data);
  }
}

// tslint:disable-next-line:variable-name
export const PaymentServiceFactory = () => new PaymentService();

export const paymentService = PaymentServiceFactory();
